import { useCallback, useState } from "react";
import { Box, Alert } from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import axios from "../utils/axios";
import { setProfile } from "../reducers/profile";

export default function GoogleSignInButton({ onSuccess, onError, activityId = undefined }) {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  const handleGoogleSuccess = useCallback(
    async (credentialResponse) => {
      try {
        setError(null);

        // Send the Google credential to our backend
        const response = await axios.post("auth/google", {
          credential: credentialResponse.credential,
        });

        // Save token to localStorage
        const token = response.data.access_token;
        if (!token) {
          throw new Error("No token returned from API");
        }

        localStorage.setItem("sva_webapp_token", token);

        // If joining an activity, do that now
        if (activityId) {
          await axios.get(`activities/join/${activityId}`, { withCredentials: true });
        }

        // Get user profile
        const profileResponse = await axios.get("auth/profile", { withCredentials: true });
        dispatch(setProfile(profileResponse.data.data));

        // Check if profile completion is required
        if (response.data.requires_profile_completion) {
          // Redirect to complete profile (handled by parent)
          if (onSuccess) {
            onSuccess({ requiresProfileCompletion: true, user: response.data.user });
          }
        } else {
          if (onSuccess) {
            onSuccess({ requiresProfileCompletion: false, user: response.data.user });
          }
        }
      } catch (err) {
        const errorData = err.response?.data;
        const errorMessage = errorData?.message || errorData?.errors?.credential?.[0] || "Google sign-in failed. Please try again.";
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    },
    [dispatch, onSuccess, onError, activityId]
  );

  const handleGoogleError = useCallback(() => {
    const errorMessage = "Google sign-in was cancelled or failed";
    setError(errorMessage);
    if (onError) {
      onError(errorMessage);
    }
  }, [onError]);

  return (
    <Box>
      <Box display="flex" justifyContent="center" width="100%">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          size="large"
          text="continue_with"
          shape="rectangular"
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

GoogleSignInButton.propTypes = {
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  activityId: PropTypes.number,
};
